import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import { Form as AntForm } from 'antd'; // Ant Design Form'u import edin
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

// Hatalı import satırını kaldırın
// import { Vendor, Office, Warehouse, Currency, TaxType } from '../../components/order/OrderHeader';

// Doğru import satırı
import { OrderType } from '../../components/order/OrderHeader';
import taxApi from '../../services/taxApi'; // taxService yerine taxApi kullanın
import OrderHeader from '../../components/order/OrderHeader';
import OrderLines from '../../components/order/OrderLines';
import OrderSummary from '../../components/order/OrderSummary';
import { customerService } from '../../services/customerService';
import { currencyApi } from '../../services/entityApi'; // currencyService yerine currencyApi kullanın
import { warehouseApi } from '../../services/entityApi'; // warehouseService yerine warehouseApi kullanın
import { officeApi } from '../../services/entityApi'; // officeService yerine officeApi kullanın
import { vendorApi } from '../../services/entityApi'; // vendorService yerine vendorApi kullanın
import { orderService } from '../../services/orderService';

// En üstte ekleyin
import { CreateOrderRequest } from '../../services/orderService';
import { productService } from '../../services/productService';
import { TaxType } from '../../services/taxApi';
import { Vendor } from '../../types/vendor';

// Office, Warehouse ve Currency arayüzlerini doğrudan tanımlayın
interface Office {
  officeCode: string;
  officeName: string;
}

interface Warehouse {
  warehouseCode: string;
  warehouseName: string;
}

interface Currency {
  currencyCode: string;
  currencyDescription: string;
  isBlocked: boolean;
}

// Form interfaces
interface Customer {
  customerCode: string;
  customerName: string;
  address: string;
}

interface Product {
  itemCode: string;
  itemName: string;
  unitPrice: number;
  unit: string;
}

// OrderLineForm arayüzüne KDV ve indirim alanları ekleyelim
interface OrderLineForm {
  id: string;
  lineNumber: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number; // KDV oranı
  discountRate: number; // İndirim oranı
  netAmount: number; // Net tutar (indirim sonrası)
  taxAmount: number; // KDV tutarı
  totalPrice: number;
  unit: string;
  description: string;
}

export interface OrderForm {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  customerAddress: string;
  status: string;
  orderType: string;
  description: string;
  lines: OrderLineForm[];
}
// Dosyanın sonundaki fonksiyonları kaldırın ve bileşen içine taşıyın
// Bileşen props'larını güncelleyin
interface OrderFormPageProps {
  orderType: string; // "WS" veya "BP"
}

const OrderFormPage: React.FC<OrderFormPageProps> = ({ orderType: propOrderType }) => {
  const navigate = useNavigate();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  
  // URL'den gelen type parametresini kullanmak yerine prop olarak gelen orderType'ı kullan
  // const orderType = searchParams.get('type') || 'wholesale';
  const orderType = propOrderType || "WS";
  const isEdit = Boolean(orderNumber);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<number>(0);
  const [headerFormValid, setHeaderFormValid] = useState<boolean>(false);
  
  // Ant Design Form hook'unu kullanın
  const [antForm] = AntForm.useForm();
  
  // State definitions
  const [form, setForm] = useState<OrderForm>({
    orderNumber: '',
    orderDate: new Date().toISOString().split('T')[0],
    customerCode: '',
    customerName: '',
    customerAddress: '',
    status: 'pending',
    orderType,
    description: '',
    lines: []
  });
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  
  // Eksik state tanımları
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState<boolean>(true);
  const [isReturn, setIsReturn] = useState<boolean>(false);
  const [isEOrder, setIsEOrder] = useState<boolean>(false);
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [loadingTaxTypes, setLoadingTaxTypes] = useState<boolean>(true);

  // Load initial data
  useEffect(() => {
    loadCustomers();
    loadProducts();
    if (isEdit && orderNumber) {
      loadOrderData(orderNumber); // orderNumber var ise yükle
    } else {
      generateOrderNumber();
    }
    loadVendors();
    loadOffices();
    loadWarehouses();
    loadCurrencies();
    loadTaxTypes();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await customerService.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getProducts({
        pageNumber: 1,
        pageSize: 1000
      });
      setProducts(response.items); // data yerine items kullanın
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };
  const loadVendors = async () => {
    try {
      const response = await vendorApi.getVendors();
      // response.data bir dizi değilse, response.data.data veya boş dizi kullan
      if (response && response.success) {
        setVendors(Array.isArray(response.data) ? response.data : []);
      } else {
        setVendors([]);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    }
  };

  const loadOffices = async () => {
    try {
      const response = await officeApi.getOffices(); // officeService yerine officeApi kullanın
      setOffices(response.data);
    } catch (error) {
      console.error('Error loading offices:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await warehouseApi.getWarehouses(); // warehouseService yerine warehouseApi kullanın
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadCurrencies = async () => {
    try {
      const response = await currencyApi.getCurrencies(); // currencyService yerine currencyApi kullanın
      setCurrencies(response.data);
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };

  const loadTaxTypes = async () => {
    try {
      const response = await taxApi.getAllTaxTypes(); // taxService.getTaxTypes() yerine taxApi.getAllTaxTypes() kullanın
      setTaxTypes(response);
    } catch (error) {
      console.error('Error loading tax types:', error);
    }
  };

  const loadOrderData = async (orderNumber: string) => {
    if (!orderNumber) return;
    
    setLoading(true);
    try {
      const response = await orderService.getOrderDetail(orderNumber);
      setForm({
        orderNumber: response.header.orderNumber,
        orderDate: response.header.orderDate.split('T')[0],
        customerCode: response.header.customerCode,
        customerName: response.header.customerName,
        customerAddress: response.header.customerAddress,
        status: response.header.status,
        orderType: response.header.orderType,
        description: response.header.description || '',
        lines: response.lines.map((line, index) => ({
          id: `line-${index}`,
          lineNumber: line.lineNumber,
          itemCode: line.itemCode,
          itemName: line.itemName,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          taxRate: line.taxRate || 18, // Varsayılan KDV oranı
          discountRate: line.discountRate || 0, // Varsayılan indirim oranı
          netAmount: line.netAmount || (line.quantity * line.unitPrice),
          taxAmount: line.taxAmount || (line.quantity * line.unitPrice * 0.18),
          totalPrice: line.totalPrice,
          unit: line.unit,
          description: line.description || ''
        }))
      });
    } catch (error) {
      console.error('Error loading order data:', error);
      enqueueSnackbar('Sipariş verileri yüklenirken hata oluştu', { variant: 'error' });
      navigate(`/orders/list?type=${orderType}`);
    } finally {
      setLoading(false);
    }
  };

  const generateOrderNumber = async () => {
    try {
      const response = await orderService.generateOrderNumber(orderType);
      setForm(prev => ({ ...prev, orderNumber: response.orderNumber }));
    } catch (error) {
      console.error('Error generating order number:', error);
    }
  };

  // Form handlers
  const handleFormChange = (field: keyof OrderForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (customerCode: string) => {
    const customer = customers.find(c => c.customerCode === customerCode);
    if (customer) {
      setForm(prev => ({
        ...prev,
        customerCode: customer.customerCode,
        customerName: customer.customerName,
        customerAddress: customer.address
      }));
    }
  };

  // addNewLine fonksiyonunu güncelleyelim
  const addNewLine = () => {
    const newLine: OrderLineForm = {
      id: `line-${Date.now()}`,
      lineNumber: form.lines.length + 1,
      itemCode: '',
      itemName: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 18, // Varsayılan KDV oranı
      discountRate: 0, // Varsayılan indirim oranı
      netAmount: 0,
      taxAmount: 0,
      totalPrice: 0,
      unit: '',
      description: ''
    };
    setForm(prev => ({
      ...prev,
      lines: [...prev.lines, newLine]
    }));
  };

  // updateLine fonksiyonunu güncelleyelim
  const updateLine = (lineId: string, field: keyof OrderLineForm, value: any) => {
    setForm(prev => ({
      ...prev,
      lines: prev.lines.map(line => {
        if (line.id === lineId) {
          const updatedLine = { ...line, [field]: value };
          // Hesaplamaları güncelle
          if (['quantity', 'unitPrice', 'taxRate', 'discountRate'].includes(field)) {
            const netAmount = updatedLine.quantity * updatedLine.unitPrice * (1 - updatedLine.discountRate / 100);
            const taxAmount = netAmount * (updatedLine.taxRate / 100);
            updatedLine.netAmount = netAmount;
            updatedLine.taxAmount = taxAmount;
            updatedLine.totalPrice = netAmount + taxAmount;
          }
          return updatedLine;
        }
        return line;
      })
    }));
  };

  // handleProductSelect fonksiyonunu güncelleyelim
  const handleProductSelect = (product: Product) => {
    const newLine: OrderLineForm = {
      id: `line-${Date.now()}`,
      lineNumber: form.lines.length + 1,
      itemCode: product.itemCode,
      itemName: product.itemName,
      quantity: 1,
      unitPrice: product.unitPrice,
      taxRate: 18, // Varsayılan KDV oranı
      discountRate: 0, // Varsayılan indirim oranı
      netAmount: product.unitPrice,
      taxAmount: product.unitPrice * 0.18,
      totalPrice: product.unitPrice * 1.18,
      unit: product.unit,
      description: ''
    };
    setForm(prev => ({
      ...prev,
      lines: [...prev.lines, newLine]
    }));
  };

  // Toplam hesaplama değişkenlerini ekleyelim
  const netTotal = form.lines.reduce((sum, line) => sum + (line.netAmount || line.quantity * line.unitPrice * (1 - (line.discountRate || 0) / 100)), 0);
  const taxTotal = form.lines.reduce((sum, line) => sum + (line.taxAmount || (line.quantity * line.unitPrice * (1 - (line.discountRate || 0) / 100)) * ((line.taxRate || 18) / 100)), 0);
  const totalAmount = form.lines.reduce((sum, line) => sum + (line.totalPrice || (line.quantity * line.unitPrice * (1 - (line.discountRate || 0) / 100)) * (1 + (line.taxRate || 18) / 100)), 0);

  // Save order - handleSave fonksiyonunu buraya taşıyalım
  const handleSave = async () => {
    if (!headerFormValid) {
      enqueueSnackbar('Lütfen tüm zorunlu alanları doldurun', { variant: 'error' });
      return;
    }
  
    if (form.lines.length === 0) {
      enqueueSnackbar('Lütfen en az bir sipariş kalemi ekleyin', { variant: 'error' });
      return;
    }
  
    setSaving(true);
    try {
      const orderData: CreateOrderRequest = {
        orderNumber: form.orderNumber,
        orderDate: form.orderDate,
        customerCode: form.customerCode,
        status: form.status,
        orderType: form.orderType,
        description: form.description,
        lines: form.lines.map(line => ({
          lineNumber: line.lineNumber,
          itemCode: line.itemCode,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
          description: line.description
        }))
      };
      
      if (isEdit) {
        await orderService.updateOrder(form.orderNumber, orderData);
        enqueueSnackbar('Sipariş başarıyla güncellendi', { variant: 'success' });
      } else {
        await orderService.createOrder(orderData);
        enqueueSnackbar('Sipariş başarıyla oluşturuldu', { variant: 'success' });
      }
      
      navigate(`/orders/list?type=${orderType}`);
    } catch (error) {
      console.error('Error saving order:', error);
      enqueueSnackbar('Sipariş kaydedilirken hata oluştu', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // OrderSummary bileşenine netTotal ve taxTotal prop'larını ekleyelim
  {activeTab === 2 && (
    <OrderSummary
      totalAmount={totalAmount}
      netTotal={netTotal}
      taxTotal={taxTotal}
      description={form.description}
      onDescriptionChange={(value) => handleFormChange('description', value)}
      onSave={handleSave}
      saving={saving}
    />
  )}

  const deleteLine = (lineId: string) => {
    setForm(prev => ({
      ...prev,
      lines: prev.lines.filter(line => line.id !== lineId).map((line, index) => ({
        ...line,
        lineNumber: index + 1
      }))
    }));
  };

  // handleTabChange fonksiyonu
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Validate before switching tabs
    if (newValue === 1 && !headerFormValid) {
      enqueueSnackbar('Lütfen önce başlık bilgilerini doldurun', { variant: 'warning' });
      return;
    }
    
    if (newValue === 2 && (!headerFormValid || form.lines.length === 0)) {
      enqueueSnackbar('Lütfen önce başlık bilgilerini doldurun ve en az bir sipariş kalemi ekleyin', { variant: 'warning' });
      return;
    }
    
    setActiveTab(newValue);
  };

  // İkinci handleSave tanımını kaldırıyoruz (459-486 satırlar arası)
  // Bu fonksiyon zaten 380. satırda tanımlanmış durumda

  const getPageTitle = () => {
    if (isEdit) return `Sipariş Düzenle - ${form.orderNumber}`;
    return `Yeni ${orderType === 'wholesale' ? 'Toptan' : 'Perakende'} Sipariş`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/orders/list?type=${orderType}`)}
          sx={{ mr: 2 }}
        >
          Geri
        </Button>
        <Typography variant="h4" component="h1">
          {getPageTitle()}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 'medium'
            }
          }}
        >
          <Tab label="BAŞLIK" />
          <Tab 
            label="SATIRLAR" 
            disabled={!headerFormValid}
          />
          <Tab 
            label="TOPLAM" 
            disabled={!headerFormValid || form.lines.length === 0}
          />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <OrderHeader
              form={antForm}
              customers={customers}
              vendors={vendors as any} // Tip uyumsuzluğunu çözmek için as any kullanıyoruz
              offices={offices}
              warehouses={warehouses}
              currencies={currencies as any}
              loadingCurrencies={loadingCurrencies}
              isReturn={isReturn}
              setIsReturn={setIsReturn}
              isEOrder={isEOrder}
              setIsEOrder={setIsEOrder}
              orderType={form.orderType as OrderType}
              taxTypes={taxTypes}
              loadingTaxTypes={loadingTaxTypes}
              onFormChange={handleFormChange}
              onCustomerChange={handleCustomerChange}
              isEdit={isEdit}
            />
          )}

          {activeTab === 1 && (
            <OrderLines
              lines={form.lines}
              products={products}
              onAddLine={addNewLine}
              onUpdateLine={updateLine}
              onDeleteLine={deleteLine}
              onProductSelect={handleProductSelect}
            />
          )}

          {activeTab === 2 && (
            <OrderSummary
              totalAmount={totalAmount}
              netTotal={netTotal}
              taxTotal={taxTotal}
              description={form.description}
              onDescriptionChange={(value) => handleFormChange('description', value)}
              onSave={handleSave}
              saving={saving}
            />
          )}
        </Box>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
          disabled={activeTab === 0}
        >
          Önceki
        </Button>
        
        {activeTab < 2 ? (
          <Button
            variant="contained"
            onClick={() => handleTabChange({} as React.SyntheticEvent, activeTab + 1)}
            disabled={
              (activeTab === 0 && !headerFormValid) ||
              (activeTab === 1 && form.lines.length === 0)
            }
          >
            İleri
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !headerFormValid || form.lines.length === 0}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default OrderFormPage;
  
